import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/auth';
import { parseAuthCallback, authCallbackErrorMessage } from '../../lib/authCallback';
import Button from '../../components/ui/Button';

const ResetPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifyingToken, setIsVerifyingToken] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [sessionEstablished, setSessionEstablished] = useState(false);

  useEffect(() => {
    const handlePasswordReset = async () => {
      setIsVerifyingToken(true);
      setError(null);

      try {
        // Supabase renvoie les jetons dans le HASH (#access_token=…) ; l'ancien code ne lisait que
        // la query string → tout lien standard était déclaré invalide. On accepte les deux.
        const cb = parseAuthCallback(window.location.hash, window.location.search, 'recovery');
        if (cb.kind === 'error') {
          setError(authCallbackErrorMessage(cb));
          setSessionEstablished(false);
          return;
        }
        if (cb.kind === 'token_hash') {
          const { data: v, error: vErr } = await supabase.auth.verifyOtp({ token_hash: cb.token_hash, type: 'recovery' });
          try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }
          if (vErr || !v.session) {
            setError(vErr?.message?.match(/expired|invalid/i) ? 'Le lien a déjà été utilisé ou a expiré. Demande un nouveau lien.' : (vErr?.message || 'Lien invalide.'));
            setSessionEstablished(false);
            return;
          }
          setSessionEstablished(true);
          return;
        }
        const accessToken = cb.kind === 'implicit' ? cb.access_token : searchParams.get('access_token');
        const refreshToken = cb.kind === 'implicit' ? cb.refresh_token : searchParams.get('refresh_token');

        if (!accessToken || !refreshToken) {
          // Session déjà posée (lien déjà traité, page rafraîchie) ?
          const { data: { session: existing } } = await supabase.auth.getSession();
          if (existing?.user) { setSessionEstablished(true); return; }
          setError('Lien de réinitialisation invalide ou expiré.');
          setSessionEstablished(false);
          return;
        }
        try { window.history.replaceState(null, '', window.location.pathname); } catch { /* ignore */ }

        // Établir la session avec les tokens
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });

        if (error) {
          console.error('Erreur lors de l\'établissement de la session:', error);
          
          if (error.message?.includes('Invalid refresh token') || error.message?.includes('expired')) {
            setError('Le lien de réinitialisation a expiré. Veuillez demander un nouveau lien.');
          } else {
            setError('Erreur lors de la vérification du lien de réinitialisation.');
          }
          setSessionEstablished(false);
          return;
        }

        if (data.session && data.user) {
          console.log('Session établie avec succès pour la réinitialisation');
          setSessionEstablished(true);
        } else {
          setError('Impossible d\'établir la session utilisateur.');
          setSessionEstablished(false);
        }

      } catch (err: any) {
        console.error('Erreur lors de la gestion de la réinitialisation:', err);
        setError('Une erreur inattendue s\'est produite.');
        setSessionEstablished(false);
      } finally {
        setIsVerifyingToken(false);
      }
    };

    handlePasswordReset();
  }, [searchParams]);

  const validatePassword = (pwd: string) => {
    if (pwd.length < 8) {
      return 'Le mot de passe doit contenir au moins 8 caractères.';
    }
    if (!/(?=.*[a-z])/.test(pwd)) {
      return 'Le mot de passe doit contenir au moins une lettre minuscule.';
    }
    if (!/(?=.*[A-Z])/.test(pwd)) {
      return 'Le mot de passe doit contenir au moins une lettre majuscule.';
    }
    if (!/(?=.*\d)/.test(pwd)) {
      return 'Le mot de passe doit contenir au moins un chiffre.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!sessionEstablished) {
      setError('Session invalide. Veuillez utiliser un nouveau lien de réinitialisation.');
      return;
    }

    // Validation des mots de passe
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setIsLoading(true);

    try {
      // Vérifier que nous avons toujours une session active
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Session expirée. Veuillez utiliser un nouveau lien de réinitialisation.');
      }

      // Mettre à jour le mot de passe
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      console.log('Mot de passe mis à jour avec succès');
      setSuccess(true);
      
      // Redirection après 3 secondes
      setTimeout(() => {
        navigate('/app');
      }, 3000);

    } catch (err: any) {
      console.error('Erreur lors de la réinitialisation du mot de passe:', err);
      setError(err.message || 'Une erreur est survenue lors de la réinitialisation de votre mot de passe.');
    } finally {
      setIsLoading(false);
    }
  };

  // État de vérification du token
  if (isVerifyingToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-gray-100 flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-6 sm:py-8 px-4 sm:px-10 shadow-xl rounded-xl border border-gray-200">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <Loader2 className="h-12 w-12 text-pink-600 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Vérification du lien de réinitialisation...
              </h2>
              <p className="text-gray-600">
                Nous vérifions votre lien de réinitialisation et établissons votre session.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-gray-100 flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-6 sm:py-8 px-4 sm:px-10 shadow-xl rounded-xl border border-gray-200">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <CheckCircle className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Mot de passe réinitialisé !
              </h2>
              <p className="text-gray-600 mb-4">
                Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Vous allez être redirigé vers le dashboard dans quelques secondes.
              </p>
              <Button onClick={() => navigate('/app')} fullWidth>
                Accéder au dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // État d'erreur (token invalide)
  if (!sessionEstablished) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-gray-100 flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-6 sm:py-8 px-4 sm:px-10 shadow-xl rounded-xl border border-gray-200">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle className="h-12 w-12 text-red-600" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Lien de réinitialisation invalide
              </h2>
              <p className="text-gray-600 mb-6">
                {error || 'Le lien de réinitialisation est invalide ou a expiré.'}
              </p>
              <div className="space-y-3">
                <Button onClick={() => navigate('/login')} fullWidth>
                  Retour à la connexion
                </Button>
                <Button variant="outline" onClick={() => window.location.reload()} fullWidth>
                  Réessayer
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Formulaire de réinitialisation du mot de passe
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-gray-100 flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="p-3 bg-pink-100 rounded-full">
            <Lock className="h-8 w-8 text-pink-600" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
          Réinitialiser votre mot de passe
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Choisissez un nouveau mot de passe sécurisé
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 sm:py-8 px-4 sm:px-10 shadow-xl rounded-xl border border-gray-200">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Nouveau mot de passe
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 sm:text-sm transition-colors"
                  placeholder="Minimum 8 caractères"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Le mot de passe doit contenir au moins 8 caractères avec majuscules, minuscules et chiffres.
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirmer le nouveau mot de passe
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 sm:text-sm transition-colors"
                  placeholder="Répétez votre nouveau mot de passe"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                fullWidth
                disabled={isLoading || !password || !confirmPassword || !sessionEstablished}
                className="py-3"
              >
                {isLoading ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-pink-600 hover:text-pink-800 font-medium"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPage;